import { VirtualRoomStorage, VirtualRoom } from './VirtualRoomStorage'
import { virtualRoomService, SyncResult } from '../../../../../../services/back4app/VirtualRoomService'
import type { TilePoint } from '../../../../../../../../types/Maps'

export interface SyncStatus {
    isOnline: boolean
    lastSyncTime: number | null
    pendingChanges: number
    syncErrors: string[]
    isSyncing: boolean
    /** A VR change reached Cloud Code in this session, so the restart notice applies (SC-422). */
    savedThisSession: boolean
}

export interface SyncOptions {
    forceSync?: boolean
    syncOnConflict?: boolean
    retryAttempts?: number
    retryDelay?: number
}

type QueueItem = { roomId: string; operation: 'create' | 'update' }

/** Editor-only fields: a change to just these isn't saved (tiles go through TileSyncManager). */
const LOCAL_ONLY_FIELDS = new Set(['outlineData', 'isActive', 'updatedAt', 'createdAt', 'dirty'])

const OBJECT_NOT_FOUND = 101

/**
 * Connects the map editor's local drafts to the map's VRs in Cloud Code (SC-422). Creates and
 * edits are drafted locally and saved on a manual sync through `saveVirtualRoom`. Deleting a VR
 * and placing its spawn point call Cloud Code at once, so a refusal (e.g. upcoming bookings in
 * the VR) shows before the editor changes anything.
 */
export class DatabaseBridge {
    private localStorage: VirtualRoomStorage
    private mapId = ''
    private syncStatus: SyncStatus = {
        isOnline: navigator.onLine,
        lastSyncTime: null,
        pendingChanges: 0,
        syncErrors: [],
        isSyncing: false,
        savedThisSession: false
    }
    private syncQueue: QueueItem[] = []
    private readonly MAX_RETRY_ATTEMPTS = 3
    private readonly RETRY_DELAY = 5000

    constructor(storage: VirtualRoomStorage = new VirtualRoomStorage()) {
        this.localStorage = storage
        this.setupOnlineOfflineHandling()
    }

    /** Opens the drafts for the editor room's map and mirrors the map's VRs into them. */
    async initialize(mapId: string): Promise<void> {
        if (!mapId) throw new Error('The map editor needs a map')
        if (mapId !== this.mapId) this.syncQueue = []
        this.mapId = mapId
        this.localStorage.setMapId(mapId)
        await this.localStorage.init()
        await this.loadRoomsFromDatabase()
    }

    getMapId(): string {
        return this.mapId
    }

    private async loadRoomsFromDatabase(): Promise<void> {
        if (!this.syncStatus.isOnline) return
        try {
            const dbRooms = await virtualRoomService.getRoomsByMapId(this.mapId)
            const localRooms = new Map((await this.localStorage.getRooms()).map(room => [room.id, room]))
            // The database decides unless a draft has unsaved edits; timestamps can't decide,
            // since the browser's clock and the server's differ and local actions bump them.
            for (const dbRoom of dbRooms) {
                const local = localRooms.get(dbRoom.roomId)
                localRooms.delete(dbRoom.roomId)
                const updatedAt = dbRoom.updatedAt?.getTime() ?? Date.now()
                if (local?.dirty) {
                    // Keep the unsaved edits, but the kind can change elsewhere (the Maps view sets
                    // the reception), and saving a stale one would be refused.
                    await this.localStorage.putRoom({ ...local, kind: dbRoom.kind })
                    this.addToSyncQueue(local.id, 'update')
                    continue
                }
                await this.localStorage.putRoom({
                    ...(local ?? { isActive: false, createdAt: dbRoom.createdAt?.getTime() ?? Date.now() }),
                    id: dbRoom.roomId,
                    mapId: this.mapId,
                    name: dbRoom.name,
                    color: dbRoom.color,
                    kind: dbRoom.kind,
                    properties: dbRoom.properties,
                    bounds: dbRoom.bounds,
                    metadata: dbRoom.metadata,
                    outlineData: undefined,
                    dirty: false,
                    updatedAt
                } as VirtualRoom)
            }
            // Drafts the database doesn't have: never-saved ones are queued again; clean ones were
            // deleted by someone else and go.
            for (const local of localRooms.values()) {
                if (local.dirty) this.addToSyncQueue(local.id, 'create')
                else await this.localStorage.deleteRoom(local.id)
            }
            this.syncStatus.lastSyncTime = Date.now()
        } catch (error) {
            console.error('[DatabaseBridge] Error loading rooms from database:', error)
            this.syncStatus.syncErrors.push(`Database load failed: ${error}`)
        }
    }

    async createRoom(room: VirtualRoom): Promise<void> {
        await this.localStorage.createRoom({ ...room, mapId: this.mapId, dirty: true })
        this.addToSyncQueue(room.id, 'create')
    }

    async updateRoom(roomId: string, updates: Partial<VirtualRoom>): Promise<void> {
        const saved = Object.keys(updates).some(key => !LOCAL_ONLY_FIELDS.has(key))
        await this.localStorage.updateRoom(roomId, saved ? { ...updates, dirty: true } : updates)
        if (saved) this.addToSyncQueue(roomId, 'update')
    }

    /**
     * Deletes the VR in Cloud Code first; only then does it leave the drafts. Throws Cloud Code's
     * error (e.g. upcoming bookings) and leaves the VR in place. A VR that was never saved has
     * nothing to delete there.
     */
    async deleteRoom(roomId: string): Promise<void> {
        try {
            await virtualRoomService.deleteRoom(roomId, this.mapId)
            this.syncStatus.savedThisSession = true
        } catch (error) {
            if ((error as { code?: number })?.code !== OBJECT_NOT_FOUND) throw error
        }
        await this.localStorage.deleteRoom(roomId)
        this.syncQueue = this.syncQueue.filter(item => item.roomId !== roomId)
        this.syncStatus.pendingChanges = this.syncQueue.length
    }

    /**
     * Saves the VR's spawn point in `metadata.spawnPoint` at once. The whole draft goes along so
     * a VR that was never synced is created with its name, not just the point.
     */
    async saveSpawnPoint(roomId: string, spawnPoint: TilePoint): Promise<void> {
        const room = await this.localStorage.getRoomById(roomId)
        if (!room) throw new Error(`Room ${roomId} is not in the editor`)
        const metadata = { ...(room.metadata || {}), spawnPoint }
        await virtualRoomService.saveRoom({
            mapId: this.mapId,
            roomId,
            name: room.name,
            color: room.color,
            properties: room.properties,
            bounds: room.bounds,
            metadata,
            ...(room.kind && room.kind !== 'reception' ? { kind: room.kind } : {})
        })
        this.syncStatus.savedThisSession = true
        await this.localStorage.updateRoom(roomId, { metadata })
    }

    async getRooms(): Promise<VirtualRoom[]> {
        return this.localStorage.getRooms()
    }

    async getRoomById(roomId: string): Promise<VirtualRoom | null> {
        return this.localStorage.getRoomById(roomId)
    }

    async setActiveRoom(roomId: string): Promise<void> {
        await this.localStorage.setActiveRoom(roomId)
    }

    async forceSync(options: SyncOptions = {}): Promise<SyncResult> {
        const { retryAttempts = this.MAX_RETRY_ATTEMPTS } = options
        if (!this.syncStatus.isOnline) {
            return {
                success: false,
                syncedCount: 0,
                errorCount: this.syncQueue.length,
                errors: ['Offline mode - cannot sync to database']
            }
        }
        this.syncStatus.isSyncing = true
        try {
            await this.cleanupOrphanedQueueItems()
            const result = await this.syncPendingChanges(retryAttempts)
            this.syncStatus.lastSyncTime = Date.now()
            this.syncStatus.pendingChanges = this.syncQueue.length
            this.syncStatus.syncErrors = result.errors
            return result
        } finally {
            this.syncStatus.isSyncing = false
        }
    }

    async manualSync(): Promise<SyncResult> {
        if (this.syncQueue.length === 0) {
            return { success: true, syncedCount: 0, errorCount: 0, errors: [] }
        }
        return this.forceSync({ forceSync: true })
    }

    private async syncPendingChanges(retryAttempts: number): Promise<SyncResult> {
        const result: SyncResult = { success: true, syncedCount: 0, errorCount: 0, errors: [] }

        for (let attempt = 0; attempt <= retryAttempts && this.syncQueue.length > 0; attempt++) {
            if (attempt > 0) await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY))
            const queue = this.syncQueue
            this.syncQueue = []
            for (const item of queue) {
                try {
                    // The whole current draft is saved: `saveVirtualRoom` creates the VR or updates
                    // it, so a VR never synced before is created with its name.
                    const data = await this.localStorage.getRoomById(item.roomId)
                    if (!data) continue
                    await virtualRoomService.saveRoom({
                        mapId: this.mapId,
                        roomId: item.roomId,
                        name: data.name,
                        color: data.color,
                        properties: data.properties,
                        bounds: data.bounds,
                        metadata: data.metadata,
                        // The reception is set from the Maps view; the editor only picks meeting or cubicle.
                        ...(data.kind && data.kind !== 'reception' ? { kind: data.kind } : {})
                    })
                    this.syncStatus.savedThisSession = true
                    await this.localStorage.updateRoom(item.roomId, { dirty: false })
                    result.syncedCount++
                } catch (error) {
                    result.errorCount++
                    result.errors.push(`${item.operation} ${item.roomId}: ${error instanceof Error ? error.message : error}`)
                    this.syncQueue.push(item)
                }
            }
        }

        result.success = this.syncQueue.length === 0
        this.syncStatus.pendingChanges = this.syncQueue.length
        return result
    }

    /** One queued save per VR: it sends the draft as it is at sync time. */
    private addToSyncQueue(roomId: string, operation: QueueItem['operation']): void {
        if (!this.syncQueue.some(item => item.roomId === roomId)) {
            this.syncQueue.push({ roomId, operation })
        }
        this.syncStatus.pendingChanges = this.syncQueue.length
    }

    private setupOnlineOfflineHandling(): void {
        window.addEventListener('online', () => {
            this.syncStatus.isOnline = true
        })
        window.addEventListener('offline', () => {
            this.syncStatus.isOnline = false
        })
    }

    getSyncStatus(): SyncStatus {
        return { ...this.syncStatus }
    }

    getPendingChangesCount(): number {
        return this.syncQueue.length
    }

    clearSyncErrors(): void {
        this.syncStatus.syncErrors = []
    }

    /** Drops queued saves for drafts that no longer exist locally. */
    async cleanupOrphanedQueueItems(): Promise<void> {
        const valid: QueueItem[] = []
        for (const item of this.syncQueue) {
            if (await this.localStorage.getRoomById(item.roomId).catch(() => null)) valid.push(item)
        }
        this.syncQueue = valid
        this.syncStatus.pendingChanges = valid.length
    }

    getDetailedSyncStatus(): SyncStatus & { queueDetails: Array<{ roomId: string; operation: string }> } {
        return {
            ...this.syncStatus,
            queueDetails: this.syncQueue.map(item => ({ roomId: item.roomId, operation: item.operation }))
        }
    }

    async exportRooms(): Promise<string> {
        return this.localStorage.exportRooms()
    }

    async importRooms(jsonData: string): Promise<void> {
        for (const room of await this.localStorage.importRooms(jsonData)) {
            this.addToSyncQueue(room.id, 'create')
        }
    }

    dispose(): void {
        this.syncQueue = []
    }
}

export const databaseBridge = new DatabaseBridge()
